import { lockScroll, unlockScroll } from '@theme/utilities';

const initializeNewsletterPopup = (root) => {
  if (root.dataset.newsletterInitialized === 'true') return;

  const dialog = root.querySelector('[data-newsletter-dialog]');
  const teaser = root.querySelector('[data-newsletter-teaser]');
  const openButton = root.querySelector('[data-newsletter-open]');
  const closeButton = root.querySelector('[data-newsletter-close]');
  const backdrop = root.querySelector('[data-newsletter-backdrop]');
  const success = root.querySelector('[data-newsletter-success]');
  const errors = root.querySelector('.dietz-newsletter__errors');
  const form = root.querySelector('.dietz-newsletter__form');
  const emailInput = root.querySelector('#DietzNewsletterEmail');
  const codeCard = root.querySelector('[data-newsletter-code-card]');
  const codeOutput = root.querySelector('[data-newsletter-code]');
  const copyButton = root.querySelector('[data-newsletter-copy]');
  const copyLabel = root.querySelector('[data-newsletter-copy-label]');
  const copyStatus = root.querySelector('[data-newsletter-copy-status]');
  const codeFallback = root.querySelector('[data-newsletter-code-fallback]');
  const dialogFocusTarget = root.querySelector('[data-newsletter-focus]');
  const pageWrapper = document.querySelector('.page-wrapper');

  if (!(dialog instanceof HTMLDialogElement) || !(teaser instanceof HTMLElement)) return;

  root.dataset.newsletterInitialized = 'true';

  const sessionGet = (key) => {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  };

  const sessionSet = (key, value) => {
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      return;
    }
  };

  const sessionRemove = (key) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      return;
    }
  };

  const pendingEmailKey = 'dietz-newsletter-pending-email';
  let scrollStyles;

  const buildDiscountCode = async (email) => {
    if (!window.crypto?.subtle || typeof TextEncoder === 'undefined') return null;

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) return null;

    const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalizedEmail));
    const hash = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, 12)
      .toUpperCase();

    return `WELCOME15-${hash}`;
  };

  const copyText = async (value) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const temporaryInput = document.createElement('textarea');
    temporaryInput.value = value;
    temporaryInput.setAttribute('readonly', '');
    temporaryInput.style.position = 'fixed';
    temporaryInput.style.opacity = '0';
    document.body.appendChild(temporaryInput);
    temporaryInput.select();
    document.execCommand('copy');
    temporaryInput.remove();
  };

  const initializeSuccessCode = async () => {
    if (!(codeCard instanceof HTMLElement) || !(codeOutput instanceof HTMLOutputElement)) return;

    const pendingEmail = sessionGet(pendingEmailKey);
    const discountCode = pendingEmail ? await buildDiscountCode(pendingEmail) : null;

    if (!discountCode) {
      if (codeFallback instanceof HTMLElement) codeFallback.hidden = false;
      return;
    }

    codeOutput.value = discountCode;
    codeCard.hidden = false;
    sessionRemove(pendingEmailKey);

    if (!(copyButton instanceof HTMLButtonElement)) return;

    copyButton.addEventListener('click', async () => {
      try {
        await copyText(discountCode);
        const copiedLabel = copyButton.dataset.copiedLabel;
        if (copyLabel instanceof HTMLElement && copiedLabel) copyLabel.textContent = copiedLabel;
        if (copyStatus instanceof HTMLElement && copiedLabel) copyStatus.textContent = copiedLabel;

        window.setTimeout(() => {
          const defaultLabel = copyButton.dataset.copyLabel;
          if (copyLabel instanceof HTMLElement && defaultLabel) copyLabel.textContent = defaultLabel;
          if (copyStatus instanceof HTMLElement) copyStatus.textContent = '';
        }, 2000);
      } catch {
        codeOutput.focus?.();
      }
    });
  };

  form?.addEventListener('submit', () => {
    if (!(emailInput instanceof HTMLInputElement) || !emailInput.validity.valid) return;

    sessionSet(pendingEmailKey, emailInput.value.trim().toLowerCase());
  });

  const lockPageScroll = () => {
    if (scrollStyles) return;

    scrollStyles = {
      bodyOverflow: document.body.style.overflow,
      pageWrapperOverflow: pageWrapper instanceof HTMLElement ? pageWrapper.style.overflow : '',
    };

    lockScroll(dialog);
    document.body.style.overflow = 'hidden';
    if (pageWrapper instanceof HTMLElement) pageWrapper.style.overflow = 'hidden';
  };

  const unlockPageScroll = () => {
    if (!scrollStyles) return;

    unlockScroll(dialog);
    document.body.style.overflow = scrollStyles.bodyOverflow;
    if (pageWrapper instanceof HTMLElement) pageWrapper.style.overflow = scrollStyles.pageWrapperOverflow;
    scrollStyles = undefined;
  };

  const openDialog = () => {
    if (dialog.open) return;

    if (typeof dialog.show === 'function') {
      dialog.show();
    } else {
      dialog.setAttribute('open', '');
    }

    root.classList.add('dietz-newsletter--open');
    lockPageScroll();
    window.requestAnimationFrame(() => {
      if (dialogFocusTarget instanceof HTMLElement) dialogFocusTarget.focus({ preventScroll: true });
    });
  };

  const closeDialog = () => {
    if (!dialog.open) return;

    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
  };

  openButton?.addEventListener('click', openDialog);
  closeButton?.addEventListener('click', closeDialog);
  backdrop?.addEventListener('click', closeDialog);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !dialog.open) return;

    event.preventDefault();
    closeDialog();
  });

  dialog.addEventListener('close', () => {
    root.classList.remove('dietz-newsletter--open');
    unlockPageScroll();
    if (success) root.hidden = true;
  });

  if (success) {
    teaser.hidden = true;
    initializeSuccessCode();
    openDialog();
    return;
  }

  if (errors) {
    openDialog();
  }
};

document.querySelectorAll('[data-newsletter-popup]').forEach(initializeNewsletterPopup);
